import { useEffect, useRef, useState } from 'react'
import { Control, FieldError, FieldErrors, useFieldArray, UseFormSetValue, useWatch } from 'react-hook-form'

import { BillingConfigFormValues } from './schema'

import styles from './BillingConfigEditor.module.scss'
import formFieldStyles from '@/components/FormField/styles.module.scss'

import { Currency, StripeProductOption } from '@/api/generated'
import Button from '@/components/Button'
import FormField from '@/components/FormField/FormField'
import Select from '@/components/Select'
import { c } from '@/utils'

// Products that have default pricing in PRICE_CONFIG_BY_CURRENCY (backend).
// FLOW_TEAM_* variants are excluded because they lack default price entries.
const SUPPORTED_PRODUCTS = new Set<StripeProductOption>([
  StripeProductOption.FlowProMonthly,
  StripeProductOption.FlowProYearly,
  StripeProductOption.FlowStudentMonthly,
  StripeProductOption.FlowStudentYearly,
  StripeProductOption.FlowBusinessMonthly,
  StripeProductOption.FlowBusinessYearly,
])

const STRIPE_PRODUCT_OPTIONS = Object.values(StripeProductOption)
  .filter((v) => SUPPORTED_PRODUCTS.has(v))
  .map((value) => ({
    value,
    label: value.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
  }))

const YEARLY_PRODUCTS = new Set<StripeProductOption>([
  StripeProductOption.FlowProYearly,
  StripeProductOption.FlowStudentYearly,
  StripeProductOption.FlowBusinessYearly,
])

const isYearlyCadence = (productName?: string) =>
  !!productName && YEARLY_PRODUCTS.has(productName as StripeProductOption)

const TRUE_UP_PERIOD_OPTIONS = [
  { value: '30', label: '30 (Monthly)' },
  { value: '90', label: '90 (Quarterly)' },
  { value: '365', label: '365 (Annual)' },
]

const CURRENCY_OPTIONS = Object.values(Currency).map((cur) => ({
  value: cur,
  label: cur.toUpperCase(),
}))

interface BillingConfigFormProps {
  control: Control<BillingConfigFormValues>
  errors: FieldErrors<BillingConfigFormValues>
  disabled?: boolean
  setValue: UseFormSetValue<BillingConfigFormValues>
}

const BillingConfigForm = ({ control, errors, disabled = false, setValue }: BillingConfigFormProps) => {
  const [isTiersExpanded, setIsTiersExpanded] = useState(false)

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'pricingTiers',
  })

  const handleAddTier = () => {
    append({ maxSeats: '', priceCentsPerSeatPerYear: '' })
    setIsTiersExpanded(true)
  }

  const tierCount = fields.length

  const trueUpPeriodDays = useWatch({ control, name: 'trueUpPeriodDays' })
  const productName = useWatch({ control, name: 'productName' })
  const currency = useWatch({ control, name: 'currency' })
  const customUnitPriceCents = useWatch({ control, name: 'customUnitPriceCents' })

  const isYearly = isYearlyCadence(productName)
  const pricePeriodLabel = isYearly ? 'year' : 'month'

  // Custom-priced configs (a per-seat override or pricing tiers) must carry an explicit Stripe price ID;
  // the optional catalog default would silently mismatch the negotiated price.
  const isCustomPriced = (customUnitPriceCents?.trim() ?? '') !== '' || tierCount > 0

  const contractPeriodDays = useWatch({ control, name: 'contractPeriodDays' })

  const nextTrueUpDate = useWatch({ control, name: 'nextTrueUpDate' })
  const contractEndDate = useWatch({ control, name: 'contractEndDate' })

  // Auto-set next true-up date when period changes, only if date is currently empty
  const prevPeriodRef = useRef(trueUpPeriodDays)
  useEffect(() => {
    if (trueUpPeriodDays > 0 && trueUpPeriodDays !== prevPeriodRef.current) {
      prevPeriodRef.current = trueUpPeriodDays
      if (!nextTrueUpDate) {
        const date = new Date()
        date.setDate(date.getDate() + trueUpPeriodDays)
        setValue('nextTrueUpDate', date.toISOString().split('T')[0], { shouldValidate: true })
      }
    }
  }, [trueUpPeriodDays, nextTrueUpDate, setValue])

  // Auto-set contract renewal date when contract length changes (debounced), only if date is currently empty
  const prevContractRef = useRef(contractPeriodDays)
  useEffect(() => {
    const days = Number(contractPeriodDays)
    if (days <= 0 || contractPeriodDays === prevContractRef.current) return

    const timer = setTimeout(() => {
      prevContractRef.current = contractPeriodDays
      if (!contractEndDate) {
        const date = new Date()
        date.setDate(date.getDate() + days)
        setValue('contractEndDate', date.toISOString().split('T')[0])
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [contractPeriodDays, contractEndDate, setValue])

  // Scroll to the first validation error on each submit attempt
  // const { submitCount } = useFormState({ control })
  useEffect(() => {
    if (Object.keys(errors).length === 0) return
    const el = document.querySelector(`.${styles.fieldError}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [errors])

  return (
    <div className={styles.billingConfigForm}>
      {/* True-up Period — above the date field */}
      <div className={formFieldStyles.formGroup}>
        <label className={formFieldStyles.label}>True-up Period (days) *</label>
        <span className={styles.hint}>How often seat usage is reconciled and invoiced.</span>
        <div className={styles.fullWidthSelect}>
          <Select
            value={trueUpPeriodDays > 0 ? String(trueUpPeriodDays) : ''}
            options={TRUE_UP_PERIOD_OPTIONS}
            onChange={(val) => setValue('trueUpPeriodDays', Number(val), { shouldValidate: true, shouldDirty: true })}
            isDisabled={disabled}
            placeholder="Select a true-up period"
          />
        </div>
        {errors.trueUpPeriodDays && <p className={styles.fieldError}>{errors.trueUpPeriodDays.message}</p>}
      </div>

      {/* Next True-up Date */}
      <FormField
        name="nextTrueUpDate"
        label="Next True-up Date"
        type="date"
        control={control}
        error={errors.nextTrueUpDate}
        disabled={disabled}
        hint="The next date when seat usage will be reconciled and invoiced. Dates are in UTC."
      />

      {/* Payment Terms */}
      <FormField
        name="paymentTermsDays"
        label="Payment Terms (days)"
        type="number"
        control={control}
        error={errors.paymentTermsDays}
        disabled={disabled}
        hint="Number of days the enterprise has to pay after an invoice is issued."
      />

      {/* Currency */}
      <div className={formFieldStyles.formGroup}>
        <label className={formFieldStyles.label}>Currency *</label>
        <span className={styles.hint}>Currency used for all invoices and pricing for this enterprise.</span>
        <div className={styles.fullWidthSelect}>
          <Select
            value={currency}
            options={CURRENCY_OPTIONS}
            onChange={(val) => setValue('currency', val as Currency, { shouldValidate: true, shouldDirty: true })}
            isDisabled={disabled}
          />
        </div>
        {errors.currency && <p className={styles.fieldError}>{errors.currency.message}</p>}
      </div>

      {/* Stripe Product dropdown */}
      <div className={formFieldStyles.formGroup}>
        <label className={formFieldStyles.label}>Stripe Product *</label>
        <span className={styles.hint}>The Stripe product used for invoicing this enterprise.</span>
        <div className={styles.fullWidthSelect}>
          <Select
            value={productName}
            options={STRIPE_PRODUCT_OPTIONS}
            onChange={(val) => setValue('productName', val, { shouldValidate: true, shouldDirty: true })}
            isDisabled={disabled}
          />
        </div>
        {errors.productName && <p className={styles.fieldError}>{errors.productName.message}</p>}
      </div>

      {/* Stripe Price ID */}
      <FormField
        name="stripePriceId"
        label="Stripe Price ID"
        control={control}
        error={errors.stripePriceId}
        placeholder="price_..."
        required={isCustomPriced}
        disabled={disabled}
        hint={
          isCustomPriced
            ? 'Required for custom prices. Needs to match price ID in Stripe'
            : `Optional. If left empty, the default price for ${productName?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) ?? 'the selected product'} (${currency?.toUpperCase()}) will be used.`
        }
      />

      {/* Custom Unit Price */}
      <FormField
        name="customUnitPriceCents"
        label={`Custom Unit Price (cents per seat/${pricePeriodLabel})`}
        type="number"
        control={control}
        placeholder="Optional"
        required={false}
        disabled={disabled}
        hint={`Optional. Override the default per-seat price billed ${isYearly ? 'annually' : 'monthly'}.`}
      />

      {/* Seat Cap */}
      <FormField
        name="seatCap"
        label="Seat Cap"
        type="number"
        control={control}
        placeholder="Optional"
        required={false}
        disabled={disabled}
        hint="Optional. Maximum number of seats allowed for this enterprise. Leave empty for no limit."
      />

      {/* Pricing Tiers — collapsible section */}
      <div className={styles.tiersSection}>
        <button
          type="button"
          className={styles.tiersHeader}
          onClick={() => setIsTiersExpanded(!isTiersExpanded)}
          aria-expanded={isTiersExpanded}
        >
          <div className={styles.tiersHeaderLeft}>
            <span className={c(styles.chevron, isTiersExpanded && styles.expanded)}>&#9654;</span>
            <span className={styles.tiersLabel}>Pricing Tiers</span>
            <span className={styles.tiersSummary}>
              {tierCount > 0 ? `${tierCount} tier${tierCount !== 1 ? 's' : ''} configured` : 'No tiers'}
            </span>
          </div>
        </button>

        {isTiersExpanded && (
          <div className={styles.tiersBody}>
            <span className={styles.hint}>
              Optional. Prices are per seat per {pricePeriodLabel} in cents. Last tier is always unlimited seats.
            </span>
            {fields.map((field, index) => {
              const isLast = index === fields.length - 1
              return (
                <div key={field.id} className={styles.tierRow}>
                  {isLast ? (
                    <div className={styles.unlimitedBadge}>
                      <span>Unlimited</span>
                    </div>
                  ) : (
                    <FormField
                      name={`pricingTiers.${index}.maxSeats`}
                      label="Max Seats"
                      type="number"
                      control={control}
                      error={errors.pricingTiers?.[index]?.maxSeats as FieldError | undefined}
                      placeholder="e.g. 500"
                      required={false}
                      disabled={disabled}
                    />
                  )}
                  <FormField
                    name={`pricingTiers.${index}.priceCentsPerSeatPerYear`}
                    label={`Price (cents/seat/${pricePeriodLabel})`}
                    type="number"
                    control={control}
                    error={errors.pricingTiers?.[index]?.priceCentsPerSeatPerYear as FieldError | undefined}
                    placeholder="e.g. 16800"
                    required={false}
                    disabled={disabled}
                  />
                  <Button type="link" title="✕" onClick={() => remove(index)} isDisabled={disabled} />
                </div>
              )
            })}

            <Button type="light" title="+ Add Tier" onClick={handleAddTier} isDisabled={disabled} />
          </div>
        )}
      </div>

      {/* Contract Length */}
      <FormField
        name="contractPeriodDays"
        label="Contract Length (days)"
        type="number"
        control={control}
        placeholder="Optional"
        required={false}
        disabled={disabled}
        hint="Optional. Total length of the contract in days."
      />

      {/* Contract Renewal Date */}
      <FormField
        name="contractEndDate"
        label="Contract Renewal Date"
        type="date"
        control={control}
        required={false}
        disabled={disabled}
        hint="Optional. When the enterprise contract ends/renews. Auto-calculated from contract length if provided. Dates are in UTC."
      />
    </div>
  )
}

export default BillingConfigForm
